import React, { useState, useRef, useEffect } from 'react';
import { shuffle } from 'lodash'; 
import styles from '../Vol1.module.css'; 
import { useNavigate } from 'react-router-dom';
import { getAudioUrl, getImageUrl } from '../services/r2Service';
import { FaPlay, FaPause, FaForward, FaBackward, FaRandom, FaRedo } from 'react-icons/fa';

interface Song {
  audioFile: string;
  coverImage: string;
  title: string;
}

interface Vol4Props {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
}

// Add type for event handlers
// type InputChangeEvent = React.ChangeEvent<HTMLInputElement>;


const songs: Song[] = [
  { 
    audioFile: getAudioUrl('megapackv4/AHcountin 09.30.20.mp3'),
    coverImage: getImageUrl('twinkle.png'),
    title: 'Shooting Stars' 
  },
  { 
    audioFile: getAudioUrl('megapackv4/complicated 07.30.21.mp3'),
    coverImage: getImageUrl('complicated.png'),
    title: 'Complicated' 
  },
  { 
    audioFile: getAudioUrl('megapackv4/mullberry 06.06.20.mp3'),
    coverImage: getImageUrl('mullberry.png'),
    title: 'Mullberry' 
  },
  { 
    audioFile: getAudioUrl('megapackv4/ragedy 02.21.20_4.mp3'),
    coverImage: getImageUrl('ragedy2.png'),
    title: 'Ragedy' 
  },
  { 
    audioFile: getAudioUrl('megapackv4/rockstar 02.28.21.mp3'),
    coverImage: getImageUrl('rockstar.png'),
    title: 'Thin Ice' 
  },
  { 
    audioFile: getAudioUrl('megapackv4/sorry 06.01.21.mp3'),
    coverImage: getImageUrl('sorry.png'),
    title: 'Sorry' 
  },
  { 
    audioFile: getAudioUrl('megapackv4/the kid 12.11.20.mp3'),
    coverImage: getImageUrl('thekid.png'),
    title: 'The Kid' 
  },
  { 
    audioFile: getAudioUrl('megapackv4/MoonLight 06.02.24.mp3'),
    coverImage: getImageUrl('moonlight.png'),
    title: 'MoonLight' 
  },
  { 
    audioFile: getAudioUrl('megapackv4/King 08.01.21.mp3'),
    coverImage: getImageUrl('King.png'),
    title: 'King' 
  },
  { 
    audioFile: getAudioUrl('megapackv4/Give My Life Meaning 06.12.23.mp3'),
    coverImage: getImageUrl('givemylifemeaning.png'),
    title: 'Give My Life Meaning' 
  },
  { 
    audioFile: getAudioUrl('megapackv4/MOBB DEEP 05.02.21.mp3'),
    coverImage: getImageUrl('mobb_deep.png'),
    title: 'MOBB DEEP' 
  },
  { 
    audioFile: getAudioUrl('megapackv4/Style 02.09.25.mp3'),
    coverImage: getImageUrl('style.png'),
    title: 'Style' 
  }
];

const Vol4: React.FC<Vol4Props> = ({ searchTerm, setSearchTerm }) => {
  const audioRef = useRef<HTMLAudioElement>(new Audio());
  const [currentSongIndex, setCurrentSongIndex] = useState(0);
  const [volume, setVolume] = useState(0.5);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [filteredSongs, setFilteredSongs] = useState(songs);
  const [isPlaying, setIsPlaying] = useState(false);
  const [repeat, setRepeat] = useState(false);
  const [shuffleEnabled, setShuffleEnabled] = useState(false);
  const [shuffledSongs, setShuffledSongs] = useState(songs);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const navigate = useNavigate();
  const prevImageUrlRef = useRef<string | null>(null);

  const handleNextTapeClick = () => {
    navigate('/Vol1');
  };

  const handlePrevTapeClick = () => {
    navigate('/Vol3');
  };

  const handleShuffle = () => {
    const audioElement = audioRef.current;
    const currentSong = shuffleEnabled ? shuffledSongs[currentSongIndex] : filteredSongs[currentSongIndex];
    const currentSongIndexInFiltered = filteredSongs.findIndex(song => song.title === currentSong.title);
    const currentPlaybackTime = audioElement.currentTime;

    if (shuffleEnabled) {
        setShuffledSongs(filteredSongs);
        setCurrentSongIndex(currentSongIndexInFiltered);
    } else {
        const upcomingSongs = shuffle(filteredSongs.filter((_, index) => index !== currentSongIndexInFiltered));
        setShuffledSongs([currentSong, ...upcomingSongs]);
        setCurrentSongIndex(0);
    }
    setShuffleEnabled(!shuffleEnabled);
    setRepeat(false);

    // Restore playback position after shuffle state changes
    setTimeout(() => {
        audioElement.currentTime = currentPlaybackTime;
        if (isPlaying) audioElement.play().catch(() => {});
    }, 0);
  };

  const handleRepeat = () => {
    const audioElement = audioRef.current;
    const currentSongs = shuffleEnabled ? shuffledSongs : filteredSongs;
    const currentSong = currentSongs[currentSongIndex];
    const currentSongIndexInFiltered = filteredSongs.findIndex(song => song.title === currentSong.title);
    const currentPlaybackTime = audioElement.currentTime;

    setRepeat(!repeat);
    setShuffleEnabled(false);
    setShuffledSongs(filteredSongs);
    setCurrentSongIndex(currentSongIndexInFiltered);

    // Restore playback position after repeat state changes
    setTimeout(() => {
        audioElement.currentTime = currentPlaybackTime;
        if (isPlaying) audioElement.play().catch(() => {});
    }, 0);
  };

  const handleSongEnd = () => {
    if (repeat) {
        // If repeat is enabled, just restart the current song
        audioRef.current.currentTime = 0;
        audioRef.current.play();
    } else {
        // If repeat is disabled, move to next song
        const nextIndex = (currentSongIndex + 1) % (shuffleEnabled ? shuffledSongs.length : filteredSongs.length);
        setCurrentSongIndex(nextIndex);
    }
  };

  useEffect(() => {
    audioRef.current.addEventListener('ended', handleSongEnd);

    return () => {
      audioRef.current.removeEventListener('ended', handleSongEnd);
    };
  }, [repeat, shuffleEnabled, currentSongIndex]);

  useEffect(() => {
    const audioElement = audioRef.current;

    const handleLoadedMetadata = () => {
      setDuration(audioElement.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audioElement.currentTime);
    };

    audioElement.addEventListener('loadedmetadata', handleLoadedMetadata);
    audioElement.addEventListener('timeupdate', handleTimeUpdate);

    return () => {
      audioElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audioElement.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, []);

  useEffect(() => {
    const currentSongs = shuffleEnabled ? shuffledSongs : filteredSongs;
    const audioElement = audioRef.current;

    if (currentSongs.length > 0 && audioElement.src !== currentSongs[currentSongIndex].audioFile) {
        audioElement.src = currentSongs[currentSongIndex].audioFile;
        // Only reset time if we're not shuffling
        if (!shuffleEnabled) {
            audioElement.currentTime = 0;
        }

        if (isPlaying) {
            audioElement.play().catch(() => {});
        }
    } else if (currentSongs.length === 0) {
        // Stop playback and reset when no songs are available
        audioElement.pause();
        audioElement.currentTime = 0;
        setIsPlaying(false);
    }
  }, [currentSongIndex, shuffleEnabled, filteredSongs, shuffledSongs]);

  useEffect(() => {
    if (searchTerm === '') {
      setFilteredSongs(songs);
      setShuffledSongs(songs);
    } else {
      const filtered = songs.filter(song => song.title.toLowerCase().includes(searchTerm.toLowerCase()));
      setFilteredSongs(filtered);
      setShuffledSongs(filtered);
    }
    setCurrentSongIndex(0);
  }, [searchTerm]);

  // Cleanup effect to stop audio when navigating away
  useEffect(() => {
    return () => {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    };
  }, []);

  const formatTime = (time: number) => {
    const floorTime = Math.floor(time);
    const minutes = Math.floor(floorTime / 60);
    const seconds = '0' + (floorTime - minutes * 60);
    return minutes + ':' + seconds.substr(-2);
  };

  const handleTogglePlay = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    audioRef.current.volume = newVolume;
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    setCurrentTime(newTime);
    audioRef.current.currentTime = newTime;
  };

  const changeSong = (delta: number) => {
    const currentSongs = shuffleEnabled ? shuffledSongs : filteredSongs;
    const nextIndex = (currentSongIndex + delta + currentSongs.length) % currentSongs.length;
    setCurrentSongIndex(nextIndex);
  };

  const selectSong = (title: string) => {
    const index = filteredSongs.findIndex(song => song.title === title);
    if (index > -1) {
      setCurrentSongIndex(index);
      setIsPlaying(true);
      audioRef.current.play().catch(() => {});
    }
  };

  // Update the image loading effect to only trigger when the cover image actually changes
  useEffect(() => {
    const currentSongs = shuffleEnabled ? shuffledSongs : filteredSongs;
    const currentSong = currentSongs[currentSongIndex];
    const currentImageUrl = currentSong?.coverImage;

    if (prevImageUrlRef.current !== currentImageUrl) {
      setIsImageLoading(true);
      prevImageUrlRef.current = currentImageUrl;
    }
  }, [currentSongIndex, shuffleEnabled, filteredSongs, shuffledSongs]);

  return (
    <div className={styles.container}>
      <h1 className={styles.volumeTitle}>Volume 4</h1>
      <input
        className={styles['search-bar']}
        type='text'
        placeholder='Search for a song...'
        value={searchTerm}
        onChange={event => setSearchTerm(event.target.value)}
      />
      <div className={styles.albumCoverContainer}>
        {filteredSongs.length > 0 ? (
          <img 
            src={shuffleEnabled ? shuffledSongs[currentSongIndex]?.coverImage : filteredSongs[currentSongIndex]?.coverImage} 
            alt='Cover' 
            className={`${styles.albumCover} ${isImageLoading ? styles.loading : ''}`}
            onLoad={() => setIsImageLoading(false)}
          />
        ) : (
          <img 
            src={getImageUrl('nophoto.jpg')} 
            alt='No Results' 
            className={styles.albumCover}
          />
        )}
      </div>

      <div className={styles.audioControls}>
        <button onClick={handleShuffle} className={styles.button} title={shuffleEnabled ? 'Disable Shuffle' : 'Enable Shuffle'} disabled={filteredSongs.length === 0}>
          <FaRandom className={shuffleEnabled ? styles.activeIcon : styles.icon} />
        </button>
        <button onClick={() => changeSong(-1)} className={styles.button} title="Previous" disabled={filteredSongs.length === 0}>
          <FaBackward className={styles.icon} />
        </button>
        <button onClick={handleTogglePlay} className={`${styles.button} ${styles.playButton}`} title={isPlaying ? 'Pause' : 'Play'} disabled={filteredSongs.length === 0}>
          {isPlaying ? <FaPause className={styles.icon} /> : <FaPlay className={styles.icon} />}
        </button>
        <button onClick={() => changeSong(1)} className={styles.button} title="Next" disabled={filteredSongs.length === 0}>
          <FaForward className={styles.icon} />
        </button>
        <button onClick={handleRepeat} className={styles.button} title={repeat ? 'Disable Repeat' : 'Enable Repeat'} disabled={filteredSongs.length === 0}>
          <FaRedo className={repeat ? styles.activeIcon : styles.icon} />
        </button>
      </div>

      <div className={styles.playerControls}>
        <div className={styles.trackTitle}>
          {filteredSongs.length > 0 
            ? (shuffleEnabled ? shuffledSongs[currentSongIndex]?.title : filteredSongs[currentSongIndex]?.title)
            : 'No songs found'}
        </div>
        <div className={styles.timeControls}>
          <div className={styles.timeRow}>
            <span className={styles.timeText}>{formatTime(currentTime)}</span>
            <input
              type="range"
              min="0"
              max={duration}
              value={currentTime}
              onChange={handleTimeChange}
              className={styles.timeSlider}
              disabled={filteredSongs.length === 0}
            />
            <span className={styles.timeTextRight}>{formatTime(duration)}</span>
          </div>
        </div>
        <div className={styles.volumeSliderWrapper}>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={handleVolumeChange}
            className={styles.volumeSlider}
          />
        </div>
        <div className={styles.volumeText}>Volume</div>
        <div style={{ height: '2rem' }} />
        <div className={styles.tapeControls}>
          <button onClick={handleNextTapeClick}>Next Tape</button>
          <button onClick={handlePrevTapeClick}>Previous Tape</button>
        </div>
      </div>

      <div className={styles.tracklist}>
        <h2 className={styles.tracklistLabel}>Tracklist</h2>
        {filteredSongs.length > 0 ? (
          filteredSongs.map(song => (
            <div key={song.title} className={styles.tracklistItem} onClick={() => selectSong(song.title)}>
              {song.title}
            </div>
          ))
        ) : (
          <p className={styles.tracklistLabel}>No songs match your search.</p>
        )}
      </div>
    </div>
  );
};

export default Vol4;
